import React, { useEffect } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import MobileBottomNav from "@/components/catalog/MobileBottomNav";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const SUPPORTED_LANGS = ["en", "fr", "de", "it", "pt", "es"];
const BASE_URL = "https://elite-horse.com";

const LanguageLayout: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Set language and html lang attribute
  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      document.documentElement.lang = lang;
    } else if (lang && !SUPPORTED_LANGS.includes(lang)) {
      const rest = location.pathname.replace(`/${lang}`, "") || "/";
      navigate(`/de${rest}${location.search}`, { replace: true });
    }
  }, [lang, i18n, navigate, location]);

  // Inject hreflang <link> tags and canonical into <head>
  useEffect(() => {
    const pathWithoutLang = lang
      ? location.pathname.replace(`/${lang}`, "") || ""
      : "";

    // Remove old hreflang links and canonical
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    // Skip hreflang on paginated pages (query params like ?page=2)
    const hasQueryParams = location.search.includes("page=");

    if (!hasQueryParams) {
      SUPPORTED_LANGS.forEach(l => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = l;
        link.href = `${BASE_URL}/${l}${pathWithoutLang}`;
        link.setAttribute("data-hreflang", "true");
        document.head.appendChild(link);
      });

      // x-default → German (primary market)
      const xDefault = document.createElement("link");
      xDefault.rel = "alternate";
      xDefault.hreflang = "x-default";
      xDefault.href = `${BASE_URL}/de${pathWithoutLang}`;
      xDefault.setAttribute("data-hreflang", "true");
      document.head.appendChild(xDefault);
    }

    // Self-referencing canonical
    let canonical = document.querySelector('link[rel="canonical"][data-managed]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("data-managed", "true");
      document.head.appendChild(canonical);
    }
    // Canonical should exclude query params for clean URLs
    canonical.setAttribute("href", `${BASE_URL}/${lang || "de"}${pathWithoutLang}`);

    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    };
  }, [lang, location.pathname, location.search]);

  return (
    <>
      <SiteHeader />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          id="main-content"
          role="main"
          className="pb-16 sm:pb-0"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <SiteFooter />
      <MobileBottomNav />
    </>
  );
};

export { SUPPORTED_LANGS };
export default LanguageLayout;
